import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MasterConfig } from 'shared-types';

interface ConfigContextType {
  config: MasterConfig | null;
  loading: boolean;
  error: string | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
    children: ReactNode;
    siteId: string;
    backendUrl: string;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children, siteId, backendUrl }) => {
  const [config, setConfig] = useState<MasterConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        if (!backendUrl) {
            // This should not happen if the build process is correct.
            // Throw an error to fail fast and make the problem obvious.
            throw new Error("ConfigProvider is missing the backendUrl prop. Check the build configuration.");
        }
        setLoading(true);
        setError(null);
        const BASE_URL = backendUrl.replace(/\/$/, '');
        const response = await fetch(`${BASE_URL}/api/config/live/${siteId}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred while fetching config.';
        console.error(errorMessage);
        setError(`Error: Could not load site configuration from the server. Please ensure the backend is running and that the REACT_APP_BACKEND_URL environment variable is correctly set in your frontend deployment. Details: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [siteId, backendUrl]);

  return (
    <ConfigContext.Provider value={{ config, loading, error }}>
      {children}
    </ConfigContext.Provider>
  );
};